/* ============================================================================
   UMAI PACK BUILDER — quantity-ladder buy box for sections/product-umai.liquid.

   Shape (True Classic's Classic Crew Neck, adapted):
     tier ladder  ->  slot grid  ->  fullscreen picker  ->  one multi-line add

   Two structural notes that are not obvious from the markup:

   1. The picker overlay is MOVED TO <body> on first open. theme.css:517 sets
      `details, header, main {overflow:hidden}` (Palo Alto base reset), which
      clips any position:fixed descendant of <main>. Left in the section the
      overlay hit-tests but never paints — the same class of bug that ate the
      hover mega-menu for a week in July.

   2. Pack prices are TOTALS in shop currency, configured in the section. They
      are display only: the checkout discount is a separate Shopify automatic
      discount that has to match the ladder. If the two disagree, the PDP is
      quoting a price the cart will not honour, so treat the config and the
      discount as one change, never two.

   Singles (qty 1) is a real tier: selecting it hands the buy column back to
   the stock PDP (native size chips + Add to Bag) rather than routing a
   one-shirt purchase through a bundle UI it does not need.
   ========================================================================== */
(function () {
  'use strict';

  var I18N = window.UMAI_I18N || {};
  function tr(k, fb) { return I18N[k] || fb; }
  /* Placeholder form for anything that mixes words with a number or a price.
     Gluing them together ("Save " + money) only reads correctly in English —
     Japanese puts the amount first ("[amount]お得"), Polish declines the noun
     after a count. Same [placeholder] convention the rest of the theme uses;
     NOT Liquid {{ }}, which renders empty when a param isn't passed. */
  function trf(k, fb, vars) {
    var s = tr(k, fb);
    for (var p in vars) s = s.split('[' + p + ']').join(vars[p]);
    return s;
  }
  /* 2026-09-07 (Ryan) — the same four-line escaper assets/umai-bundle.js has
     always had; this file never got one. The picker builds its cards by gluing
     catalogue text (product titles from products.json, variant size labels,
     CDN image URLs, the /cart/add.js error copy) straight into innerHTML.
     Nothing is broken today and the ladder is switched off besides, but the
     catalogue already contains straight double quotes (8"x10" posters) and
     apostrophes (Knight's Duty, "Small (US men's 5–6.5)"), and the day one of
     those lands in a slot an unescaped quote breaks the attribute it sits in.
     A "<" in a title would inject markup for every shopper on the page.
     Escape on the way into HTML, always — textContent stays fine as it is. */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* presentment-aware money. products.json reports SHOP-currency dollar
     strings and the tier config is authored in shop currency, so both go
     through the same rate conversion the free-shipping line uses. */
  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency) || {};
    var rate = parseFloat(cur.rate) || 1;
    var v = cents * rate;
    if (cur.active && cur.active !== 'USD') {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur.active }).format(v / 100); } catch (e) {}
    }
    var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    return fmt.replace(/<[^>]+>/g, '').replace(/\{\{\s*amount[^}]*\}\}/, (v / 100).toFixed(2)).trim();
  }

  /* ============================================================================
     TELEMETRY
     Nothing about the picker was visible in analytics — you could see AOV move
     but not WHY, and the decision that matters most (is the 6-Pack too big?)
     only shows up as drop-off by slot position. Every event carries `slot` and
     `filled` for exactly that.

     Fans out to whatever the page already has rather than adding a dependency:
     GA4 via dataLayer, Meta as a custom event, Klaviyo for segmentation and the
     abandoned-bundle flow. Each is guarded — a blocked pixel must never take the
     picker down with it.

     2026-09-07 (Ryan) — CORRECTION to the paragraph above: the dataLayer and
     fbq branches have never reached anything. GA4 and Meta run on this store as
     Shopify Customer Events pixels, inside a sandboxed frame that cannot see
     this page's window.dataLayer or window.fbq — the served HTML has no
     googletagmanager, no gtag and no connect.facebook.net anywhere. Klaviyo's
     _learnq shim was the only branch that worked. Shopify.analytics.publish is
     the one bridge from theme code into that sandbox, so it is added below; the
     two dead lines stay because they cost nothing and would start working the
     day a GTM container or a real Meta pixel is put on the page directly.
     ⚠ HALF OF THIS FIX IS IN ADMIN: publish() is a no-op until a Custom Pixel
     (Settings -> Customer events) subscribes to these umai_pack_* names and
     forwards them to GA4/Meta. Without that pixel the data still goes nowhere.
     ========================================================================== */
  function track(name, data) {
    var ev = 'umai_pack_' + name;
    var payload = data || {};
    try {
      window.dataLayer = window.dataLayer || [];
      var row = { event: ev };
      for (var k in payload) row[k] = payload[k];
      window.dataLayer.push(row);
    } catch (e) {}
    try { if (typeof window.fbq === 'function') window.fbq('trackCustom', ev, payload); } catch (e) {}
    try {
      if (window._learnq) window._learnq.push(['track', ev, payload]);
    } catch (e) {}
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish(ev, payload);
    } catch (e) {}
    /* a page-level hook for anything else that wants it (Shopify web pixel,
       Intelligems, a future A/B tool) without another integration in here */
    try { document.dispatchEvent(new CustomEvent(ev, { detail: payload })); } catch (e) {}
  }

  function init(root) {
    var cfgEl = root.querySelector('[data-upk-config]');
    if (!cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    if (!cfg.tiers || !cfg.tiers.length) return;

    var pdp = root.closest('[data-umai-pdp]') || document;
    var tiersEl = root.querySelector('[data-upk-tiers]');
    var qtyEl = root.querySelector('[data-upk-qty]');
    var slotsEl = root.querySelector('[data-upk-slots]');
    var countEl = root.querySelector('[data-upk-count]');
    var sumEl = root.querySelector('[data-upk-sum]');
    var ctaEl = root.querySelector('[data-upk-cta]');
    var errEl = root.querySelector('[data-upk-err]');
    var noteEl = root.querySelector('[data-upk-note]');
    /* stock buy-column pieces the pack UI takes over from */
    var nativeBits = [].slice.call(pdp.querySelectorAll('.up-optlbl, .up-chips, .up-ctarow, .up-hint, [data-up-b2g]'));

    var tierIdx = 0;
    cfg.tiers.forEach(function (t, i) { if (t.preselect) tierIdx = i; });
    /* PICKS ARE NEVER TRUNCATED. The visible bundle is the first `qty` entries
       of this array, so dropping from a 6-pack to a 3-pack (or bouncing off
       Singles to compare prices) hides the extra shirts rather than deleting
       them — going back up restores the whole selection. Truncating here cost
       a shopper five picks for one curious tap. */
    var picked = [];  /* {pid,vid,title,img,size} | null, sparse */

    /* ---- the bundle survives navigation ----
       Building a 6-pack means visiting product pages, and every visit used to
       wipe the slots because `picked` only lived in memory. sessionStorage (not
       local) so it lasts the visit and not forever — a bundle abandoned days ago
       should not reappear priced against shirts that may have changed. Cleared
       once the pack is actually in the cart. */
    var STORE_KEY = 'umai:pack:v1';
    function saveBundle() {
      try {
        if (!picked.some(Boolean)) { sessionStorage.removeItem(STORE_KEY); return; }
        sessionStorage.setItem(STORE_KEY, JSON.stringify({ q: cfg.tiers[tierIdx].qty, p: picked }));
      } catch (e) {}
    }
    function restoreBundle() {
      try {
        var raw = sessionStorage.getItem(STORE_KEY);
        if (!raw) return;
        var d = JSON.parse(raw);
        if (!d || !d.p || !d.p.some(Boolean)) return;
        picked = d.p;
        /* land on the tier they were building, if it still exists */
        for (var i = 0; i < cfg.tiers.length; i++) if (cfg.tiers[i].qty === d.q) { tierIdx = i; break; }
      } catch (e) {}
    }
    function clearBundle() {
      picked = [];
      try { sessionStorage.removeItem(STORE_KEY); } catch (e) {}
    }

    /* ---- CART CREDIT ----
       The ladder is a CART-level quantity discount, so tees already in the bag
       count toward it. Ignoring them meant the shopper closest to the next tier
       got told the longest story: 3 tees in the bag, already paying $30 each,
       and the page still said "pick 6" — when 3 more would have landed them on
       $24.99. Same isTee rule as the cart ladder and the discount's collection
       scope: title says T-Shirt, does not say Oversized. */
    var cartRows = [], cartSig = null;   /* one entry per shirt already in the bag */
    function isCartTee(it) {
      /* delegate to the one shared rule so a pack-priced blank in the bag is not
         credited toward a DIFFERENT product's pack — see umai-offer.liquid */
      if (window.UMAI_OFFER && typeof window.UMAI_OFFER.eligible === 'function') return window.UMAI_OFFER.eligible(it);
      var t = it.product_title || it.title || '';
      return /t-?shirt/i.test(t) && !/oversized/i.test(t);
    }
    function readCart() {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var rows = [];
          ((c && c.items) || []).forEach(function (it) {
            if (!isCartTee(it)) return;
            var img = it.image || (it.featured_image && it.featured_image.url) || '';
            if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=200';
            /* a line of 3 is three shirts — expand it so the slots match */
            for (var q = 0; q < it.quantity; q++) {
              rows.push({ img: img, title: it.product_title || it.title || '', size: it.variant_title || '' });
            }
          });
          /* compare CONTENT, not just count — swapping one shirt for another
             keeps the length identical and would have left a stale thumbnail
             sitting in a credited slot */
          var sig = rows.map(function (r) { return r.title + '|' + r.size; }).join('~');
          if (sig === cartSig) return;
          cartSig = sig;
          cartRows = rows;
          /* the credited slots shift, so anything picked into them must go */
          picked.length = 0;
          renderSlots(); renderSum(); if (ov) syncOvCount();
        }).catch(function () {});
    }

    function tierQty() { return cfg.tiers[tierIdx].qty; }
    /* How many of this tier's slots the bag already covers. The pack always
       shows its FULL size — collapsing a 6-Pack to "0/3" because three were
       already in the bag threw away the whole point of the tier. Instead the
       first slots are filled with the actual shirts from the bag, so "3/6" and
       three empty squares say "add three more" without needing a sentence. */
    function credited() {
      if (tierQty() <= 1) return 0;
      return Math.min(cartRows.length, tierQty());
    }
    function creditActive() { return credited() > 0; }
    function slotCount() { return tierQty(); }
    /* credited slots count as filled — they are already paid for in the bag */
    function filledCount() {
      var n = credited();
      for (var i = credited(); i < slotCount(); i++) if (picked[i]) n++;
      return n;
    }
    /* how many the shopper still has to choose */
    function toPick() { return slotCount() - filledCount(); }

    /* ---------------- tier ladder ---------------- */
    function renderTiers() {
      tiersEl.innerHTML = '';
      cfg.tiers.forEach(function (t, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'upk-tier';
        b.setAttribute('aria-pressed', String(i === tierIdx));
        var save = t.qty > 1 ? (cfg.unit * t.qty - t.price) : 0;
        b.innerHTML =
          (t.badge ? '<span class="bd">' + esc(t.badge) + '</span>' : '') +
          '<span class="q">' + (t.qty > 1 ? trf('pack_n_pack', '[n]-Pack', { n: t.qty }) : tr('pack_singles', 'Singles')) + '</span>' +
          '<span class="s' + (save > 0 ? '' : ' mut') + '">' +
            (save > 0 ? trf('pack_save', 'Save [amount]', { amount: money(save) })
                      : trf('pack_each', '[amount] each', { amount: money(cfg.unit) })) +
          '</span>';
        b.addEventListener('click', function () { setTier(i); });
        tiersEl.appendChild(b);
      });
    }

    function setTier(i) {
      if (tierIdx !== i) track('tier', { from: cfg.tiers[tierIdx].qty, to: cfg.tiers[i].qty, value: cfg.tiers[i].price / 100 });
      tierIdx = i;
      var t = cfg.tiers[i];
      root.classList.toggle('upk-is-single', t.qty <= 1);
      nativeBits.forEach(function (el) { el.style.display = t.qty > 1 ? 'none' : ''; });
      if (qtyEl) qtyEl.textContent = t.qty;
      renderTiers(); renderSlots(); renderSum();
    }

    /* ---------------- slot grid ---------------- */
    function renderSlots() {
      slotsEl.innerHTML = '';
      var n = slotCount();
      var cr = credited();
      for (var i = 0; i < n; i++) (function (i) {
        /* slots the bag already covers: show the real shirt, tagged and locked.
           No remove cross — it lives in the cart, and pretending otherwise
           would let someone "remove" a shirt here that is still being charged. */
        if (i < cr) {
          var cItem = cartRows[i];
          var d = document.createElement('div');
          d.className = 'upk-slot filled in-bag';
          d.setAttribute('aria-label', cItem.title + ' — ' + tr('pack_in_bag_tag', 'In bag'));
          d.innerHTML = (cItem.img ? '<img src="' + esc(cItem.img) + '" alt="" loading="lazy">' : '') +
            '<span class="bag">' + tr('pack_in_bag_tag', 'In bag') + '</span>';
          slotsEl.appendChild(d);
          return;
        }
        var s = picked[i];
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'upk-slot' + (s ? ' filled' : '');
        b.setAttribute('aria-label', s ? s.title + ' — ' + s.size : tr('pack_add_shirt', 'Add a shirt'));
        b.innerHTML = s
          ? '<img src="' + esc(s.img) + '" alt="" loading="lazy"><span class="sz">' + esc(s.size) + '</span><span class="rm" aria-hidden="true">&#10005;</span>'
          : '<span class="plus" aria-hidden="true">+</span>';
        b.addEventListener('click', function (e) {
          /* tapping the corner cross clears the slot; anywhere else re-picks.
             Clearing must also AIM at the hole it just made — without that,
             activeSlot still pointed at wherever the last pick landed and the
             next choice silently overwrote a filled slot instead of filling
             the empty one, so the bundle never got back to full. */
          if (s && e.target.closest('.rm')) {
            picked[i] = null; activeSlot = i;
            renderSlots(); renderSum(); syncOvCount(); return;
          }
          openPicker(i);
        });
        slotsEl.appendChild(b);
      })(i);
      if (countEl) {
        var f = filledCount();
        countEl.textContent = f + ' / ' + n;
        countEl.classList.toggle('full', f === n && n > 0);
      }
      /* single persistence hook — renderSlots runs after every mutation */
      saveBundle();
    }

    /* ---------------- price recap + CTA ---------------- */
    function renderSum() {
      var t = cfg.tiers[tierIdx];
      if (t.qty <= 1) { sumEl.innerHTML = ''; ctaEl.style.display = 'none'; return; }
      ctaEl.style.display = '';
      var was = cfg.unit * t.qty;
      sumEl.innerHTML =
        '<span class="l">' +
          (was > t.price ? '<span class="was num">' + money(was) + '</span>' : '') +
          '<span class="now num">' + money(t.price) + '</span>' +
        '</span>' +
        /* floor, not round: $149.99 over 6 is $24.998, and rounding would print
           "$25.00/item" under a $149.99 headline. Flooring gives $24.99 — the
           number True Classic shows, and the one that can never quote a
           per-shirt price above what the pack actually works out to. */
        '<span class="per num">' + money(Math.floor(t.price / t.qty)) + tr('pack_per_item', '/item') + '</span>';
      /* the note has to sit outside sumEl — renderSum rewrites that innerHTML */
      var cr = credited();
      if (noteEl) {
        var per = money(Math.floor(t.price / t.qty));
        if (cr > 0 && cr < t.qty) {
          noteEl.textContent = trf('pack_in_bag', '[n] in your bag — add [m] more for [amount] each',
            { n: cr, m: t.qty - cr, amount: per });
          noteEl.hidden = false;
        } else if (cr >= t.qty && t.qty > 1) {
          noteEl.textContent = trf('pack_unlocked', "You've unlocked [amount]/item", { amount: per });
          noteEl.hidden = false;
        } else { noteEl.hidden = true; noteEl.textContent = ''; }
      }
      var gap = toPick();
      /* the pack is already complete out of the bag — there is nothing to add,
         so send them to the bag rather than leaving a dead button */
      if (cr >= t.qty && t.qty > 1) {
        ctaEl.disabled = false;
        ctaEl.textContent = tr('pack_view_bag', 'View Bag');
        ctaEl.dataset.viewBag = '1';
        return;
      }
      delete ctaEl.dataset.viewBag;
      ctaEl.disabled = false;
      ctaEl.textContent = gap > 0
        ? trf(gap === 1 ? 'pack_pick_one' : 'pack_pick_many',
              gap === 1 ? 'Pick [n] shirt' : 'Pick [n] shirts', { n: gap })
        /* Never quote the full pack price when the bag is part-paid for it —
           they are adding fewer shirts than the tier, so the tier total is not
           what this click costs. */
        : cr > 0
          ? trf('pack_add_n', 'Add [n] to Bag', { n: t.qty - cr })
          : trf('pack_add_bag', 'Add to Bag — [amount]', { amount: money(t.price) });
    }

    /* ============================================================
       PICKER — built once, parented to <body> (see header note)
       ============================================================ */
    var ov = null, ovGrid = null, ovCount = null, ovSearch = null, ovMore = null;
    var ovStrip = null, ovDone = null, ovDoneTxt = null, ovCheckout = null;
    /* Three genuinely distinct views, not three orderings of the same list.
       All = the whole catalogue in curated order; Best Sellers = the ~30
       hand-ranked trending shirts; New = published inside newDays (22 today). */
    var SORTS = [
      { k: 'new',  label: tr('pack_sort_new',  'New') },
      { k: 'best', label: tr('pack_sort_best', 'Best Sellers') },
      { k: 'all',  label: tr('pack_sort_all',  'All') }
    ];
    var sortKey = 'new';
    /* Tagged members are known at render; everyone else gets re-checked against
       the cart, so joining UMAI+ mid-session unlocks the shirts without a
       reload. Mirrors the past-exclusive PDP gate. */
    var isMember = !!cfg.member;
    function refreshMembership() {
      if (isMember) return;
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var has = ((c && c.items) || []).some(function (it) {
            return !!it.handle && it.handle.indexOf('inveterate-subscription') === 0;
          });
          if (has && !isMember) { isMember = true; renderedKey = null; if (ov) paint(); }
        }).catch(function () {});
    }
    refreshMembership();
    document.addEventListener('umaiplus:added', function () { isMember = true; renderedKey = null; if (ov) paint(); });
    document.addEventListener('cart:updated', refreshMembership);
    var loaded = [];        /* products.json rows already fetched */
    var page = 0, exhausted = false, loading = false, activeSlot = 0;
    var PAGE_CARDS = 48, shown = PAGE_CARDS;   /* cards currently built into the grid */

    function buildOverlay() {
      ov = document.createElement('div');
      ov.className = 'upk-ov';
      ov.setAttribute('role', 'dialog');
      ov.setAttribute('aria-modal', 'true');
      ov.setAttribute('aria-label', tr('pack_picker', 'Choose your shirts'));
      ov.innerHTML =
        '<div class="upk-ovh">' +
          '<div class="upk-ovt">' +
            '<b>' + tr('pack_picker', 'Choose your shirts') + '</b>' +
            '<span class="ct" data-upk-ovcount></span>' +
            '<button type="button" class="upk-ovx" data-upk-close aria-label="' + tr('pack_close', 'Close') + '">&#10005;</button>' +
          '</div>' +
          '<input type="search" class="upk-srch" data-upk-search placeholder="' + tr('pack_search', 'Search shirts…') + '" autocomplete="off">' +
          '<div class="upk-sorts" data-upk-sorts role="group" aria-label="' + tr('pack_sort', 'Sort') + '"></div>' +
          '<div class="upk-strip" data-upk-strip></div>' +
          /* translated + threshold-filled in Liquid, so the shipping figure can
             never drift from the setting the rest of the theme reads */
          ((cfg.trust && cfg.trust.length)
            ? '<div class="upk-trust">' + cfg.trust.map(function (s) { return '<span>' + s + '</span>'; }).join('') + '</div>'
            : '') +
        '</div>' +
        '<div class="upk-ovb"><div class="upk-grid" data-upk-grid></div>' +
          '<button type="button" class="upk-more" data-upk-more hidden>' + tr('pack_more', 'Load more') + '</button>' +
        '</div>' +
        '<div class="upk-done" data-upk-done>' +
          '<div class="t" data-upk-donetxt></div>' +
          '<button type="button" data-upk-checkout></button>' +
        '</div>';
      document.body.appendChild(ov);
      ovGrid = ov.querySelector('[data-upk-grid]');
      ovCount = ov.querySelector('[data-upk-ovcount]');
      ovSearch = ov.querySelector('[data-upk-search]');
      ovMore = ov.querySelector('[data-upk-more]');
      ovStrip = ov.querySelector('[data-upk-strip]');
      ovDone = ov.querySelector('[data-upk-done]');
      ovDoneTxt = ov.querySelector('[data-upk-donetxt]');
      ovCheckout = ov.querySelector('[data-upk-checkout]');
      if (cfg.cols) ov.style.setProperty('--upk-cols', cfg.cols);

      /* sort chips. "Best sellers" leans on the curated arrival order rather
         than a sales field products.json does not expose — ymal-trending is
         hand-ranked from real sales, so rank IS the bestseller list. */
      var sortsEl = ov.querySelector('[data-upk-sorts]');
      SORTS.forEach(function (s) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'upk-sort';
        b.textContent = s.label; b.setAttribute('aria-pressed', String(s.k === sortKey));
        b.addEventListener('click', function () {
          if (sortKey !== s.k) track('sort', { tab: s.k });
          sortKey = s.k; shown = PAGE_CARDS;
          /* A sort is a different question from a search. Leaving the query in
             the box meant tapping "Best Sellers" after searching still showed
             the one search result — the chip lit up and the grid did not
             change, which reads as the tab being broken. */
          if (ovSearch) ovSearch.value = '';
          sortsEl.querySelectorAll('.upk-sort').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
          /* one request, not a whole-catalogue sweep — Shopify does the date
             sort, so New paints as fast as any other tab */
          paint();
          if (s.k === 'new' && !newSeq) fetchNewPage().then(paint);
          ov.querySelector('.upk-ovb').scrollTop = 0;
        });
        sortsEl.appendChild(b);
      });

      ovCheckout.addEventListener('click', function () { addPack(true); });
      ov.querySelector('[data-upk-close]').addEventListener('click', closePicker);
      ovMore.addEventListener('click', function () {
        if (ovMore.dataset.busy) return;
        ovMore.dataset.busy = '1';
        var lbl = ovMore.textContent;
        ovMore.textContent = tr('pack_loading', 'Loading…');
        /* raise the cap past what is ALREADY loaded, not just past the current
           cap — otherwise a click can spend its whole budget on rows that are
           already on screen and appear to do nothing */
        shown = Math.max(shown, ovGrid.querySelectorAll('.upk-card').length) + PAGE_CARDS;
        fetchMore().then(paint).then(function () {
          ovMore.textContent = lbl; delete ovMore.dataset.busy;
        });
      });
      /* infinite scroll: build the next batch of cards first, and only reach
         for another 2.5MB page of JSON once the shopper has actually run out
         of loaded shirts */
      var ovb = ov.querySelector('.upk-ovb');
      ovb.addEventListener('scroll', function () {
        if (ovb.scrollTop + ovb.clientHeight < ovb.scrollHeight - 400) return;
        if (loading) return;
        var built = ovGrid.querySelectorAll('.upk-card').length;
        /* built < shown means every loaded shirt is already on screen, so the
           shopper needs more DATA, not more cards. The reverse guard here made
           the grid a dead end at 30 shirts. */
        if (built < shown) { fetchMore().then(paint); return; }
        shown += PAGE_CARDS;
        paint();
      }, { passive: true });
      /* typing must search the WHOLE catalogue, not just page 1 — pull the
         rest in the background the moment a real query starts */
      var st = null;
      ovSearch.addEventListener('input', function () {
        clearTimeout(st);
        st = setTimeout(function () {
          shown = PAGE_CARDS;   /* a new query starts at the top of its own results */
          /* Paint FIRST, against whatever is already loaded. Waiting for
             loadAll() to page the whole catalogue meant a search sat there
             showing the unfiltered grid for ~10 seconds and read as broken.
             The full sweep still runs and repaints when it lands. */
          paint();
          if (ovSearch.value.trim().length >= 2 && !exhausted) loadAll().then(paint);
        }, 140);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && ov.classList.contains('on')) closePicker();
      });
    }

    /* Order matters more than completeness here. The picker opens on the same
       curated ranking the You May Also Like grid uses — ymal-trending (hand-
       ranked from real sales) then ymal-fill (all tees, best-selling sort) —
       and only then falls through to the raw collection. Straight collection
       order is roughly alphabetical for anything without behavioural data, so
       the first screen of a 500-shirt picker was arbitrary. Proven sellers
       should be the shirts a shopper sees while filling six slots. */
    /* ---- LOADING BUDGET ----
       products.json is a fat endpoint: every row carries all variants, all
       images and the full body_html, so a 250-product page is ~2.5MB. Fetching
       trending + fill + the whole collection up front cost 5.4MB and 1.8s
       before a single card painted.

       So: trending only, small, paint. Everything else arrives behind the
       first screen — fill at idle, the raw collection only when the shopper
       scrolls past what is loaded or types a search. The opening screen is the
       curated ranking either way, which is the part that has to be instant. */
    var seeded = false, filled = false, bootstrapped = false;
    var seen = {}, collSeq = 0;
    function absorb(rows, src) {
      var fresh = [];
      (rows || []).forEach(function (p) {
        if (p && !seen[p.id]) {
          /* store the ROW, not a flag — a later feed (the date-sorted one) needs
             to stamp its ordering onto a shirt another tab already loaded */
          seen[p.id] = p;
          /* arrival order IS the curated ranking (trending, then evergreen,
             then raw collection) — stamped so a sort can return to it */
          p._r = loaded.length + fresh.length;
          /* rows that came out of ymal-trending. Only 10 products carry the
             `best-seller` tag, which is far too thin to fill a 6-pack from, so
             the Best Sellers view is the hand-ranked trending set plus anything
             explicitly tagged. */
          p._trend = src === 'trend';
          /* ymal-fill = all tees in Shopify's best-selling order, so these
             continue the Best Sellers view once the hand-ranked 30 run out */
          p._fill = src === 'fill';
          /* position in the MAIN collection — the store's own merchandised
             order. This is what the All tab sorts by, and it is the only thing
             that makes All genuinely different from Best Sellers: both used to
             sort by arrival order, which starts with the trending ranking, so
             the two tabs opened on an identical first screen. */
          if (src === 'coll') p._c = collSeq++;
          fresh.push(p);
        }
      });
      loaded = loaded.concat(fresh);
      return fresh.length;
    }
    /* One internal row shape, whichever endpoint answered. The slim view uses
       short keys (t/a/img/pub) purely to keep the payload down; everything
       downstream sees the same object either way. */
    function norm(r) {
      var img = r.img || '';
      if (!img && r.images && r.images[0] && r.images[0].src) {
        img = r.images[0].src;
        img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=400';
      }
      return {
        id: r.id,
        title: r.title || '',
        img: img,
        tags: r.tags || [],
        pubMs: r.pub ? r.pub * 1000 : Date.parse(r.published_at || ''),
        memberOnly: r.x === 1,
        variants: (r.variants || []).map(function (v) {
          return { id: v.id, title: v.t != null ? v.t : v.title, available: v.a != null ? v.a : v.available };
        })
      };
    }

    /* templates/collection.upkjson.liquid — six fields instead of the whole
       product. If it is ever deleted the fetch falls through to Shopify's own
       products.json, so the picker degrades to slow rather than to broken. */
    /* `ok:false` means the REQUEST failed; it does not mean the collection ran
       out. Callers must not treat the two the same — a single rate-limited
       response used to set `exhausted` and cap the picker at whatever it had
       for the rest of the session, with no error and no way to retry.
       Shopify returns 429 with an HTML body under load, which is exactly how
       this surfaces. */
    var slimDead = false;
    function slim(handle, pg, sortBy) {
      var url = '/collections/' + handle + '?view=upkjson&page=' + (pg || 1) +
                (sortBy ? '&sort_by=' + sortBy : '');
      var fallback = function () {
        return fetch('/collections/' + handle + '/products.json?limit=50&page=' + (pg || 1))
          .then(function (r) {
            if (!r.ok) throw 0;
            return r.json();
          })
          .then(function (d) { return { products: ((d && d.products) || []).map(norm), pages: 99, ok: true }; })
          .catch(function () { return { products: [], pages: 0, ok: false }; });
      };
      if (slimDead) return fallback();
      return fetch(url).then(function (r) {
        /* only a real 404 means the template is gone and the fallback should
           become permanent — a 429 or a blip must stay retryable */
        if (r.status === 404) { slimDead = true; throw 0; }
        if (!r.ok) throw 1;
        return r.text();
      })
        .then(function (t) {
          var d = JSON.parse(t);
          if (!d || !d.products) throw 1;
          return { products: d.products.map(norm), pages: d.pages || 1, ok: true };
        })
        .catch(function (why) {
          if (why === 0) return fallback();           /* template missing */
          return { products: [], pages: 0, ok: false }; /* transient — retry later */
        });
    }

    /* the inline first screen — absorbed at init, so the very first open has
       cards to paint before any request is made */
    /* the inline first screen IS collection page 1, so start the cursor past it */
    if (cfg.seed && cfg.seed.length) { absorb(cfg.seed.map(norm), 'coll'); page = 1; }

    /* trending is what Best Sellers ranks by; it always comes over the network
       now that the inline seed is the collection */
    function seed() {
      if (seeded) return Promise.resolve();
      seeded = true;
      return slim(cfg.trend || 'ymal-trending', 1).then(function (d) { absorb(d.products, 'trend'); });
    }
    /* The evergreen list is PAGED, not a one-shot. Best Sellers used to be just
       the ~24 hand-ranked trending shirts with Load more hidden, because there
       was nothing behind it — but a shopper filling a 6-pack wants to keep
       going. ymal-fill is all tees in Shopify's best-selling order, so paging it
       is a genuine continuation of the same ranking rather than a different
       list wearing the same label. */
    var fillPage = 0, fillExhausted = false;
    function seedFill() {
      if (fillExhausted || loading) return inflight || Promise.resolve();
      loading = true; fillPage += 1;
      inflight = slim(cfg.fill || 'ymal-fill', fillPage).then(function (d) {
        if (!d.ok) { fillPage -= 1; loading = false; inflight = null; return; }
        if (!d.products.length || fillPage >= d.pages) fillExhausted = true;
        absorb(d.products, 'fill');
        filled = true;
        loading = false; inflight = null;
      });
      return inflight;
    }

    /* ---- NEW: sorted by Shopify, not by us ----
       New used to work by loading the ENTIRE catalogue (18 sequential requests,
       ~10 seconds) and then sorting locally, because you cannot know the newest
       shirt until you have seen them all. You can if the server sorts: the same
       collection with sort_by=created-descending hands back the newest twenty in
       one request. `_n` is the position in that feed. */
    var newPage = 0, newExhausted = false, newSeq = 0;
    function fetchNewPage() {
      if (newExhausted) return Promise.resolve();
      if (loading) return inflight || Promise.resolve();
      loading = true; newPage += 1;
      inflight = slim(cfg.collection, newPage, 'created-descending').then(function (d) {
        if (!d.ok) { newPage -= 1; loading = false; inflight = null; return; }
        if (!d.products.length || newPage >= d.pages) newExhausted = true;
        d.products.forEach(function (p) {
          /* stamp order BEFORE absorb dedupes — a shirt already loaded by
             another tab still needs its position in the date ranking */
          var ex = seen[p.id];
          if (ex) { if (ex._n == null) ex._n = newSeq++; }
          else p._n = newSeq++;
        });
        absorb(d.products, 'new');
        loading = false; inflight = null;
      });
      return inflight;
    }

    /* which well to draw from depends on the tab you are looking at */
    function fetchMore() {
      if (sortKey === 'best') return seedFill();
      if (sortKey === 'new') return fetchNewPage();
      return fetchPage();
    }

    /* Returning a bare resolved promise while a request was already in flight
       made the FIRST "Load more" click a no-op: the background fill was still
       running, so the click resolved instantly against nothing and the grid did
       not move. Hand back the in-flight promise instead, so a click that lands
       mid-fetch chains onto it rather than being swallowed. */
    var inflight = null;
    function fetchPage(depth) {
      if (exhausted) return Promise.resolve();
      if (loading) return inflight || Promise.resolve();
      loading = true; page += 1;
      inflight = slim(cfg.collection, page).then(function (d) {
        if (!d.ok) {
          /* request failed — step the cursor back so the same page is retried
             rather than skipped, and do NOT declare the catalogue exhausted */
          page -= 1; loading = false; inflight = null;
          return;
        }
        if (!d.products.length || page >= d.pages) exhausted = true;
        var fresh = absorb(d.products, 'coll');
        loading = false; inflight = null;
        /* The early collection pages overlap almost entirely with the trending
           and evergreen sets already loaded, so a page can legitimately add
           zero new shirts — which looked exactly like "Load more is broken".
           Keep paging until something new lands, bounded so a fully-duplicated
           collection cannot spin. */
        if (!fresh && !exhausted && (depth || 0) < 8) return fetchPage((depth || 0) + 1);
      });
      return inflight;
    }
    /* Pages the whole catalogue so a search can see every shirt. Two guards,
       both learned the hard way:
       - ONE chain at a time. Every keystroke past two characters used to start
         its own sweep, so a five-letter query fired five overlapping runs of up
         to eighteen requests each. That is what a storefront rate limiter is
         for, and hitting it takes the picker down for the whole session.
       - STOP after three fetches that add nothing. With failed requests now
         retryable (they no longer set `exhausted`), an unbounded loop would
         hammer a rate-limited store forever. */
    var allRunning = null, failStreak = 0;
    function loadAll() {
      if (allRunning) return allRunning;
      var step = function () {
        return seed().then(seedFill).then(function () {
          if (exhausted || failStreak >= 3) return;
          var before = loaded.length;
          return fetchPage().then(function () {
            if (loaded.length === before) failStreak++; else failStreak = 0;
            return step();
          });
        });
      };
      allRunning = step().then(
        function () { allRunning = null; },
        function () { allRunning = null; }
      );
      return allRunning;
    }

    /* products.json has no "is this a plain front-print tee" flag, so mirror
       the rule the rest of the theme uses (cart ladder, YMAL, sale badge):
       title says T-Shirt and does not say Oversized. */
    function isTee(p) { return /t-?shirt/i.test(p.title || '') && !/oversized/i.test(p.title || ''); }

    /* Same chip rules as umai-card-grid.liquid: NEW! wins, then the weekly
       exclusive, then best seller — one chip maximum, never a stack. If those
       rules change there, change them here too or the picker and the
       collection grid will label the same shirt two different ways. */
    function badgeFor(p) {
      var L = cfg.labels || {};
      var pub = p.pubMs;
      if (pub && !isNaN(pub) && (Date.now() - pub) / 86400000 < (cfg.newDays || 30)) return { c: 'nw', t: L['new'] || 'NEW!' };
      var tags = p.tags || [];
      if (tags.indexOf('weeklyexclusivecustom') > -1) return { c: 'red', t: L.exclusive || 'Exclusive' };
      /* Best Seller badge removed 2026-08-25 (Ryan) — every surface, see the note
         in snippets/umai-card-grid.liquid. The `best-seller` tag still ORDERS this
         picker; it just no longer draws a chip. */
      return null;
    }

    /* one card, built once and never rebuilt while the view holds still */
    var renderedKey = null, renderedCount = 0, renderedIds = [];
    function buildCard(p) {
      /* image is already CDN-resized to 400px in norm() — the raw sources are
         1080px 2.7MB PNG exports and a grid of them unresized is unusable */
      var img = p.img;
      var bd = badgeFor(p);
      var c = document.createElement('div');
      c.className = 'upk-card';
      c.innerHTML =
        '<div class="im">' + (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" decoding="async">' : '') +
          (bd ? '<span class="upk-chip ' + bd.c + '">' + bd.t + '</span>' : '') +
          (p.id === cfg.current.id ? '<span class="pin">' + tr('pack_this_one', 'This one') + '</span>' : '') +
        '</div>' +
        '<div class="tx"><div class="tt">' + esc(p.title || '') + '</div></div>' +
        '<div class="upk-szs"></div>';
      var szWrap = c.querySelector('.upk-szs');
      (p.variants || []).forEach(function (v) {
        var s = document.createElement('button');
        s.type = 'button';
        s.className = 'upk-sz' + (v.available ? '' : ' oos');
        s.textContent = v.title;
        if (v.available) s.addEventListener('click', function (e) {
          e.stopPropagation();
          fillSlot({ pid: p.id, vid: v.id, title: p.title, img: img, size: v.title });
        });
        szWrap.appendChild(s);
      });
      c.addEventListener('click', function () {
        var already = c.classList.contains('picking');
        ovGrid.querySelectorAll('.upk-card.picking').forEach(function (x) { x.classList.remove('picking'); });
        c.classList.toggle('picking', !already);
      });
      return c;
    }

    function paint() {
      var q = (ovSearch.value || '').trim().toLowerCase();
      var rows = loaded.filter(function (p) {
        if (!isTee(p)) return false;
        if (!(p.variants || []).some(function (v) { return v.available; })) return false;
        /* Members-only shirts never reach a non-member — not in any tab, not in
           search. The picker was handing every past exclusive to everyone and
           letting them buy it, which gives away the main reason to pay for
           UMAI+. Applied before every other rule so nothing can route round it. */
        if (p.memberOnly && !isMember) return false;
        if (q) return (p.title || '').toLowerCase().indexOf(q) > -1;  /* search spans everything */
        if (sortKey === 'best') return p._trend || p._fill || (p.tags || []).indexOf('best-seller') > -1;
        /* New is a SORT, not a 30-day filter — as a filter it was 22 shirts with
           nothing behind Load more, which read as the tab being broken */
        return true;
      });
      rows.sort(function (a, b) {
        /* the shirt they were already looking at always leads — it is the one
           product we know they want, and hunting for it in 500 cards is absurd */
        var pin = (b.id === cfg.current.id ? 1 : 0) - (a.id === cfg.current.id ? 1 : 0);
        if (pin) return pin;
        if (sortKey === 'new') {
          /* server ranking wins; anything it has not covered yet falls back to
             its own publish date so the first screen is never wrong, just coarse */
          var an = a._n == null ? 1e9 : a._n, bn = b._n == null ? 1e9 : b._n;
          if (an !== bn) return an - bn;
          return (b.pubMs || 0) - (a.pubMs || 0);
        }
        if (sortKey === 'all') {
          /* the store's own collection order; anything not yet seen in the
             collection (trending rows that arrived first) sorts after it */
          var ac = a._c == null ? 1e9 : a._c, bc = b._c == null ? 1e9 : b._c;
          if (ac !== bc) return ac - bc;
        }
        return a._r - b._r;   /* curated arrival order — Best Sellers ranking */
      });

      /* ---- INCREMENTAL RENDER ----
         This used to wipe the grid (`innerHTML = ''`) and rebuild every card on
         every paint. Since scrolling and Load more both call paint, appending
         twenty shirts meant destroying and recreating a hundred: every <img>
         re-requested and re-decoded, the grid height briefly collapsed to zero
         so the scroll position jumped, and any open size picker slammed shut.
         That is the "glitches out when you scroll" behaviour.
         Now: rebuild only when the view actually changes (different tab or
         query), otherwise append just the cards that are genuinely new. */
      var total = rows.length;
      var slice = rows.slice(0, shown);
      var key = sortKey + '|' + q;
      /* Appending is only safe while the rows ALREADY on screen stay in the same
         order. They do for scroll and Load more; they do NOT when a late feed
         arrives carrying shirts that sort ahead of what is rendered — the New
         tab's date-sorted page landed after the first paint and got stuck on the
         end instead of moving to the top. Compare the prefix and rebuild if it
         moved; otherwise append and keep the no-flicker path. */
      var moved = false;
      for (var k = 0; k < renderedCount && k < slice.length; k++) {
        if (renderedIds[k] !== slice[k].id) { moved = true; break; }
      }
      if (key !== renderedKey || slice.length < renderedCount || moved) {
        ovGrid.innerHTML = '';
        renderedCount = 0;
        renderedIds = [];
        renderedKey = key;
      }
      if (!total) {
        ovGrid.innerHTML = '<p class="upk-empty">' + (loading ? tr('pack_loading', 'Loading…') : tr('pack_none', 'No shirts match that search.')) + '</p>';
        renderedCount = 0;
      } else {
        if (renderedCount === 0 && ovGrid.querySelector('.upk-empty')) ovGrid.innerHTML = '';
        var frag = document.createDocumentFragment();
        for (var i = renderedCount; i < slice.length; i++) {
          frag.appendChild(buildCard(slice[i]));
          renderedIds[i] = slice[i].id;
        }
        ovGrid.appendChild(frag);
        renderedCount = slice.length;
      }
      /* Best Sellers pages ymal-fill, All pages the full collection, so both can
         genuinely load more. New has already swept the whole catalogue and a
         search spans everything, so in those the button would do nothing. */
      var wellDry = sortKey === 'best' ? fillExhausted : (sortKey === 'new' ? newExhausted : exhausted);
      ovMore.hidden = !!q || (wellDry && shown >= total);
      syncOvCount();
    }

    function syncOvCount() {
      if (ovCount) ovCount.textContent = filledCount() + ' / ' + slotCount();
      renderStrip();
      renderDone();
    }

    /* the bundle, mirrored into the picker header. Tapping a square retargets
       the next pick at it; tapping its cross clears it. */
    function renderStrip() {
      if (!ovStrip) return;
      ovStrip.innerHTML = '';
      var n = slotCount(), cr = credited();
      for (var i = 0; i < n; i++) (function (i) {
        if (i < cr) {
          var cItem = cartRows[i];
          var d = document.createElement('div');
          d.className = 'upk-mini filled in-bag';
          d.setAttribute('aria-label', cItem.title + ' — ' + tr('pack_in_bag_tag', 'In bag'));
          d.innerHTML = cItem.img ? '<img src="' + esc(cItem.img) + '" alt="" loading="lazy">' : '';
          ovStrip.appendChild(d);
          return;
        }
        var s = picked[i];
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'upk-mini' + (s ? ' filled' : '') + (i === activeSlot ? ' active' : '');
        b.setAttribute('aria-label', (s ? s.title + ' — ' + s.size : tr('pack_add_shirt', 'Add a shirt')) + ' (' + (i + 1) + '/' + n + ')');
        b.innerHTML = s
          ? '<img src="' + esc(s.img) + '" alt="" loading="lazy"><span class="x" aria-hidden="true">&#10005;</span>'
          : '<span class="p" aria-hidden="true">+</span>';
        b.addEventListener('click', function (e) {
          /* same rule as the PDP grid: clearing aims at the hole it just made */
          if (s && e.target.closest('.x')) {
            picked[i] = null; activeSlot = i;
            renderSlots(); renderSum(); syncOvCount(); return;
          }
          activeSlot = i;
          ovGrid.querySelectorAll('.upk-card.picking').forEach(function (x) { x.classList.remove('picking'); });
          renderStrip();
        });
        ovStrip.appendChild(b);
      })(i);
    }

    /* Completed-bundle bar. Only appears once every slot is full, so it can
       never read as a shortcut past an unfinished pack. */
    function renderDone() {
      if (!ovDone) return;
      var t = cfg.tiers[tierIdx];
      var done = filledCount() === slotCount() && t.qty > 1;
      ovDone.classList.toggle('on', done);
      if (!done) return;
      ovDoneTxt.innerHTML = trf('pack_complete', '[n]-Pack complete', { n: t.qty }) +
        '<span>' + money(t.price) + ' &middot; ' + money(Math.floor(t.price / t.qty)) + tr('pack_per_item', '/item') + '</span>';
      ovCheckout.textContent = tr('pack_checkout', 'Checkout');
    }
    /* index of the first empty slot inside the ACTIVE tier, or -1 when full.
       Not picked.indexOf(null) — that array is sparse and can run past the
       current tier, which used to send the picker to a slot nobody can see. */
    function nextGap() {
      /* never point at a credited slot — those belong to the bag, not the picker */
      for (var i = credited(); i < slotCount(); i++) if (!picked[i]) return i;
      return -1;
    }

    function fillSlot(item) {
      /* slot position is the whole point — drop-off by slot is what tells you
         the pack is too deep, and nothing else in analytics can show it */
      track('add_shirt', {
        product: item.title, size: item.size, slot: activeSlot + 1,
        of: slotCount(), tier: tierQty(), sort: sortKey
      });
      picked[activeSlot] = item;
      /* jump to the next gap so a 6-pack is six taps, not six round trips.
         When the last slot lands the picker STAYS OPEN and raises the
         completed-bundle bar instead of closing — the whole pack is meant to be
         built in one continuous scroll, ending at Checkout without ever
         bouncing back to the product page. */
      var next = nextGap();
      if (next !== -1) activeSlot = next;
      renderSlots(); renderSum(); syncOvCount();
      /* cartRows.length, NOT `cartTees` — that name was never declared anywhere
         in this file, so under 'use strict' reading it threw a ReferenceError on
         the very tap that COMPLETED the pack. Same typo, worse consequence, in
         addPack below. (2026-08-11) */
      if (next === -1) track('complete', { tier: tierQty(), adding: slotCount(), value: cfg.tiers[tierIdx].price / 100, in_cart: cartRows.length });
      ovGrid.querySelectorAll('.upk-card.picking').forEach(function (x) { x.classList.remove('picking'); });
    }

    function openPicker(i) {
      activeSlot = i;
      if (!ov) buildOverlay();
      track('open', { slot: i + 1, of: slotCount(), filled: filledCount(), tier: tierQty() });
      shown = PAGE_CARDS;
      ov.classList.add('on');
      document.body.style.overflow = 'hidden';
      syncOvCount();
      /* Paint the inline first screen immediately, then pull the tail in behind
         it — ONCE per page, tracked by its own flag. Gating this on
         `loaded.length` looked right and was wrong: the inline seed makes that
         non-zero before the first open, so the background load never ran and the
         picker was stuck at 24 shirts until the shopper scrolled. */
      paint();
      if (!bootstrapped) {
        bootstrapped = true;
        /* plain timer, not requestIdleCallback: rIC kept getting starved while
           the grid's images decoded, so the tail never arrived. At 14KB the
           slim page is not worth deferring for. */
        /* order matters: the main collection first, because All is the tab you
           land on and it sorts by collection position; the evergreen list after,
           because it only feeds Best Sellers. */
        /* New is the tab you land on, so its feed goes first; the collection
           order (All) and the best-selling list follow in the background */
        fetchNewPage().then(paint).then(function () {
          setTimeout(function () {
            seed().then(paint)
              .then(function () { return fetchPage(); }).then(paint)
              .then(function () { return seedFill(); }).then(paint);
          }, 200);
        });
      }
      setTimeout(function () { ovSearch && ovSearch.focus({ preventScroll: true }); }, 60);
    }
    function closePicker() {
      if (!ov) return;
      /* closing on a part-built pack is the abandon signal — how far they got
         before quitting is the number that sizes the next tier */
      var f = filledCount(), n = slotCount();
      if (f < n) track('abandon', { filled: f, of: n, tier: tierQty(), sort: sortKey });
      ov.classList.remove('on');
      document.body.style.overflow = '';
    }

    /* ---------------- add the whole pack ----------------
       One path for both buttons: the PDP's Add to Bag and the picker's
       Checkout. goCheckout skips the drawer and lands on /checkout. */
    var adding = false;
    function addPack(goCheckout) {
      errEl.textContent = '';
      var t = cfg.tiers[tierIdx];
      /* only the slots the shopper actually chose get added — the credited ones
         are already in the bag and adding them again would double-charge */
      var want = t.qty - credited();
      var gap = nextGap();
      if (gap !== -1) {
        var el = slotsEl.children[gap];
        if (el) { el.classList.remove('flash'); void el.offsetWidth; el.classList.add('flash'); }
        openPicker(gap);
        return;
      }
      if (adding) return;
      adding = true;
      var btn = goCheckout ? ovCheckout : ctaEl;
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = tr('pdp_adding', 'Adding…');
      fetch(((window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: picked.filter(Boolean).slice(0, want).map(function (s) { return { id: s.vid, quantity: 1 }; }) })
      }).then(function (r) {
        if (!r.ok) return r.text().then(function (tx) {
          var m = ''; try { var j = JSON.parse(tx); m = (j && typeof j.description === 'string') ? j.description : ''; } catch (e) {}
          throw new Error(m || tr('pdp_add_error', 'Could not add to bag — please try again.'));
        }, function () { throw new Error(tr('pdp_add_error', 'Could not add to bag — please try again.')); });
        /* ⚠ `cartTees` here was an undeclared name — a ReferenceError thrown
           INSIDE the success handler, AFTER the shirts were already in the cart
           but BEFORE clearBundle(), the "✓ Added" state and the /checkout
           redirect. The .catch below then showed the shopper
           "cartTees is not defined" on an add that had actually worked, left the
           slots full so a second tap double-added the whole pack, and stopped
           the picker's Checkout button from ever navigating. Shipped 2026-08-07,
           found 2026-08-11 driving the real file against a local cart. */
        track(goCheckout ? 'checkout' : 'add_to_cart', {
          tier: t.qty, added: want, in_cart_before: cartRows.length,
          value: t.price / 100, sort: sortKey
        });
        /* the pack is in the cart now — a stale bundle sitting in the slots
           invites adding the same six shirts a second time */
        clearBundle();
        renderSlots(); renderSum();
        if (goCheckout) {
          /* leave the button in its working state — the navigation is the
             confirmation, and resetting it first just flickers */
          /* Shopify.routes.root carries the market/locale prefix ("/" or
             "/ja-jp/"); a bare "/checkout" would drop a Japanese shopper back
             into the US market mid-funnel */
          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          window.location.href = root + 'checkout';
          return;
        }
        btn.textContent = '✓ ' + tr('pack_added', 'Added');
        setTimeout(function () { btn.textContent = label; btn.disabled = false; adding = false; }, 1400);
      }).catch(function (err) {
        btn.textContent = label; btn.disabled = false; adding = false;
        var msg = (err && err.message && err.message.length < 200) ? err.message : tr('pdp_add_error', 'Could not add to bag — please try again.');
        errEl.textContent = msg;
        /* the PDP error box is behind the overlay when checkout fails, so say
           it where the shopper is actually looking */
        if (goCheckout && ovDoneTxt) ovDoneTxt.innerHTML = '<b style="color:#D92C12">' + esc(msg) + '</b>';
      });
    }
    ctaEl.addEventListener('click', function () {
      if (ctaEl.dataset.viewBag) {
        var root2 = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        window.location.href = root2 + 'cart';
        return;
      }
      addPack(false);
    });

    restoreBundle();   /* must run before setTier — it can change tierIdx */
    setTier(tierIdx);
    readCart();
    /* the drawer fires cart:updated after every mutation; cart:refresh is the
       bag page's own signal. Both can change how many shirts still count. */
    var cartT = null;
    ['cart:updated', 'cart:refresh'].forEach(function (ev) {
      document.addEventListener(ev, function () { clearTimeout(cartT); cartT = setTimeout(readCart, 250); });
    });
    track('view', { tier: cfg.tiers[tierIdx].qty, price: cfg.tiers[tierIdx].price });
  }

  function boot() { document.querySelectorAll('[data-upk]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
